import { PlayerRef } from '@remotion/player';
import React, { useCallback, useEffect } from 'react';
import { ToolButton } from '@crabs-ai/ds-react';
import { SplitScene } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import {
  useAllItems,
  useSelectedItems,
  useTracks,
  useWriteContext,
} from '../utils/use-context';
import { useTimelinePosition } from '../utils/use-timeline-position';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';
import { applyTimelineEdit } from '@waka/compat/timeline-editing';
import { itemOwner } from '@waka/compat/group-split';

export function SplitItemTool({
  playerRef,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
}) {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const { selectedItems } = useSelectedItems();
  const { items } = useAllItems();
  const { tracks } = useTracks();
  const currentFrame = useTimelinePosition({ playerRef });
  const canSplit = selectedItems.some((id) => {
    const item = items[id];
    const track = itemOwner({ undoableState: { tracks, items } }, id)?.track;
    return (
      item &&
      track &&
      !track.locked &&
      currentFrame > item.from &&
      currentFrame < item.from + item.durationInFrames
    );
  });
  const split = useCallback(() => {
    if (!canSplit) return;
    setState({
      update: (state) =>
        applyTimelineEdit(state, selectedItems, currentFrame, 'split'),
      commitToUndoStack: true,
    });
  }, [canSplit, setState, selectedItems, currentFrame]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.isComposing || isEventTargetInputElement(event) || event.altKey)
        return;
      if (event.code === 'KeyB' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        split();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [split]);
  return (
    <ToolButton
      size="sm"
      isTransparent={false}
      state={canSplit ? 'default' : 'disabled'}
      onClick={split}
      disabled={!canSplit}
      isShowTooltip
      tooltipNode={t('Editor.timeline-controls.cut')}
      tooltipSubNode="[E / ⌘/Ctrl+B]"
      tooltipPosition="top"
    >
      <SplitScene className="size-5" />
    </ToolButton>
  );
}
